import React from 'react';
import ReactDOM from 'react-dom/client';
import { PortfolioConnectWidget } from './components/PortfolioConnectWidget';
import type {
  PortfolioConnectConfig,
  ParsedData,
  ParseMetadata,
  PortfolioConnectError,
  PortfolioConnectEvent,
  EventMetadata,
  SubmitInput,
} from './types';

/**
 * Configuration for the imperative open() API.
 * Used for non-React environments (Vanilla JS, Angular, Vue, etc.)
 */
export interface ImperativeConfig {
  /**
   * Authenticate with a short-lived access token (`at_*`, minted via
   * `POST /v1/token` from your backend — recommended for browser
   * embeds) or pass a raw API key via `apiKey` below. Both are sent
   * as the `x-api-key` header. At least one is required.
   */
  accessToken?: string;
  /** See `accessToken`. Equivalent alternative; both accepted. */
  apiKey?: string;
  /**
   * Override the API base URL. Used for dedicated or self-hosted
   * CASParser instances. Defaults to `https://api.casparser.in`.
   */
  apiBaseUrl?: string;
  /** Widget configuration options */
  config?: PortfolioConnectConfig;
  /**
   * Custom submit handler. When provided, replaces the default parse API
   * call across all intercept-capable flows (upload / inbox / inbound /
   * CDSL fetch). Inspect `input.kind` to branch between `File` and URL
   * inputs. Return value is forwarded to `onSuccess`; throw to trigger
   * `onError`.
   */
  onSubmit?: (
    input: SubmitInput,
    password: string,
    onProgress?: (percent: number) => void,
  ) => Promise<any>;
  /** Called when widget is opened */
  onOpen?: () => void;
  /** Called for all widget events (useful for analytics) */
  onEvent?: (event: PortfolioConnectEvent | string, metadata: EventMetadata) => void;
}

/**
 * Discriminated result returned by `open()`. The promise resolves once,
 * with one of three statuses, instead of rejecting on close. Lets
 * consumers handle the cancel case without try/catch.
 */
export type OpenResult =
  | {
      status: 'success';
      /** Parsed portfolio data. */
      data: ParsedData;
      /** Parsing metadata (source, duration, etc.). */
      metadata: ParseMetadata;
    }
  | {
      status: 'closed';
      /** True when the user dismissed the modal without an import. */
      cancelled: true;
    }
  | {
      status: 'error';
      /** Structured error from the SDK / API. */
      error: PortfolioConnectError;
    };

/**
 * Handle returned from create() for manual control
 */
export interface WidgetHandle {
  /** Open the widget modal */
  open: () => void;
  /** Close the widget and cleanup */
  destroy: () => void;
}

// Track active widget instance for cleanup. Note: only one open()
// can be in flight at a time. A second concurrent open() will tear
// down the first instance — its promise stays pending forever, so
// callers should serialise.
let activeContainer: HTMLDivElement | null = null;
let activeRoot: ReactDOM.Root | null = null;

function cleanup(): void {
  if (activeRoot) {
    activeRoot.unmount();
    activeRoot = null;
  }
  if (activeContainer && activeContainer.parentNode) {
    activeContainer.parentNode.removeChild(activeContainer);
    activeContainer = null;
  }
}

/**
 * Opens the Portfolio Connect widget and returns a promise that resolves
 * with a discriminated `OpenResult` (success / closed / error).
 *
 * The promise **never rejects on user-close** — it resolves with
 * `{ status: 'closed', cancelled: true }`. Real errors resolve with
 * `{ status: 'error', error }`. Use `result.status` to branch.
 */
export function open(config: ImperativeConfig): Promise<OpenResult> {
  return new Promise((resolve) => {
    cleanup();

    activeContainer = document.createElement('div');
    activeContainer.id = 'portfolio-connect-root';
    activeContainer.setAttribute('data-portfolio-connect', 'true');
    document.body.appendChild(activeContainer);

    activeRoot = ReactDOM.createRoot(activeContainer);

    config.onOpen?.();

    // `settled` guards against the success → close double-resolve race
    // (success fires onSuccess; React then unmounts which calls onClose).
    let settled = false;
    const settle = (result: OpenResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    activeRoot.render(
      React.createElement(PortfolioConnectWidget, {
        isOpen: true,
        apiKey: config.accessToken || config.apiKey || '',
        apiBaseUrl: config.apiBaseUrl,
        config: config.config,
        onSubmit: config.onSubmit,
        onSuccess: (data: ParsedData, metadata: ParseMetadata) => {
          settle({ status: 'success', data, metadata });
        },
        onClose: () => {
          settle({ status: 'closed', cancelled: true });
        },
        onError: (error: PortfolioConnectError) => {
          settle({ status: 'error', error });
        },
        onEvent: config.onEvent,
      })
    );
  });
}

/**
 * Creates a widget handle for manual control. Useful when you need to
 * open the same widget repeatedly without rebuilding the React root.
 */
export function create(config: ImperativeConfig & {
  onSuccess: (data: ParsedData, metadata: ParseMetadata) => void;
  onError?: (error: PortfolioConnectError) => void;
  onClose?: () => void;
}): WidgetHandle {
  let container: HTMLDivElement | null = null;
  let root: ReactDOM.Root | null = null;

  function render(open: boolean): void {
    if (!container) {
      container = document.createElement('div');
      container.id = 'portfolio-connect-root';
      container.setAttribute('data-portfolio-connect', 'true');
      document.body.appendChild(container);
      root = ReactDOM.createRoot(container);
    }

    root!.render(
      React.createElement(PortfolioConnectWidget, {
        isOpen: open,
        apiKey: config.accessToken || config.apiKey || '',
        apiBaseUrl: config.apiBaseUrl,
        config: config.config,
        onSubmit: config.onSubmit,
        onSuccess: (data: ParsedData, metadata: ParseMetadata) => {
          render(false);
          config.onSuccess(data, metadata);
        },
        onClose: () => {
          render(false);
          config.onClose?.();
        },
        onError: (error: PortfolioConnectError) => {
          render(false);
          config.onError?.(error);
        },
        onEvent: config.onEvent,
      })
    );
  }

  return {
    open: () => {
      config.onOpen?.();
      render(true);
    },
    destroy: () => {
      if (root) {
        root.unmount();
        root = null;
      }
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
        container = null;
      }
    },
  };
}
